<script setup lang="ts">
useHead({
  script: [
    {
      src: "https://www.googletagmanager.com/gtag/js?id=G-MWGQ9X0YKB",
      async: true,
    },
  ],
});

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const route = useRoute();
const router = useRouter();
const appConfig = useAppConfig();
const businessStore = useBusinessStore();

if (import.meta.client) {
  router.afterEach((to, from) => {
    if (to.path === from.path) return;
    nextTick(() => {
      document
        .querySelector<HTMLElement>(".business-scroll-container")
        ?.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
  });
}
const { business, isMenuOpen, giftCardSelected, selectedLocation } =
  storeToRefs(businessStore);
const globalStore = useGlobalStore();
const { progress, isLoading } = storeToRefs(globalStore);
const subheaderTitle = computed(
  () => route.meta.subheader as string | undefined,
);
const isBackButtonVisible = computed(() => route.query.reschedule !== "true");
const selectedLocationData = computed(() => selectedLocation.value);

// Search engine visibility is opt-in per business, switched on from the admin
// app. Until it is, the storefront stays reachable by URL but is kept out of
// search results: the API drops it from sitemap.xml, and this marks every one
// of its pages noindex. The test is "not explicitly enabled" rather than
// "explicitly disabled" so a business whose cached payload predates the flag
// also stays out — the safe direction for an opt-in setting. Guarded on
// `business` so pages that share this layout without one are unaffected.
// Written as a `useHead` getter, not `useSeoMeta`, for two reasons: the layout
// is not remounted when the visitor moves between storefronts, so the value
// has to stay reactive; and emitting no tag at all when SEO is on leaves the
// env-wide robots meta from nuxt.config.ts (staging/dev noindex) untouched,
// which a `useSeoMeta` field cannot express.
useHead(() => ({
  meta:
    business.value && business.value.seo_enabled !== true
      ? [{ name: "robots", content: "noindex, nofollow" }]
      : [],
}));

const hasFooter = computed(() => {
  const keys = (route.meta.keys as string[] | undefined) ?? [];
  if (keys.includes("select-employee")) return true;
  if (keys.includes("booking")) return true;
  if (keys.includes("memberships")) return true;
  if (keys.includes("order")) return true;
  if (keys.includes("gift-cards") && giftCardSelected.value) return true;
  return false;
});

const bodyPadding = computed(() => {
  const keys = (route.meta.keys as string[] | undefined) ?? []
  if (keys.includes('about-us')) return ''

  return hasFooter.value && !business.value?.is_freemium
    ? "px-4 pt-3 xs:px-5 xs:pt-5 pb-30"
    : "px-4 py-3 xs:px-5 xs:py-5"
})

const themes = ["default", "persian-green", "cosmic", "amaranth", "velvet", "amethyst", "monochrome", "blush", "sage", "lavender", "powder-blue", "noir-rose"];

const applyTheme = () => {
  appConfig.ui.primary =
    selectedLocation.value?.theme_name &&
      themes.includes(selectedLocation.value?.theme_name)
      ? selectedLocation.value?.theme_name
      : "default";
};

// Each SSR request gets its own Nuxt app instance with its own reactive
// appConfig, so this mutation is safe during server rendering and eliminates
// the flash of default color before hydration.
applyTheme();

if (import.meta.client) {
  // Re-apply when the user navigates between locations without a full remount.
  watch(() => selectedLocation.value?.theme_name, applyTheme);
}

if (business.value && selectedLocationData.value) {
  const loc = selectedLocationData.value;
  // Prefer the location's own description, fall back to the business one.
  const desc = loc.description || business.value.description || undefined;

  // Note: we intentionally don't set `title` here — app.vue's titleTemplate
  // resolves the title chunk through `t("meta." + chunk)`, so a literal
  // business name would be mangled into `meta.<name>`. The document <title>
  // is owned by the pages / app.vue default; here we only own the social +
  // description meta, which are unaffected by the template.
  useSeoMeta({
    description: desc,
    ogType: "website",
    ogTitle: loc.name,
    ogDescription: desc,
    ogImage: loc.cover || loc.logo || undefined,
    twitterCard: "summary_large_image",
    twitterTitle: loc.name,
    twitterDescription: desc,
  });

  // LocalBusiness structured data so the storefront is eligible for rich
  // results / map listings. Hand-rolled JSON-LD (no schema-org module) to
  // stay compatible with the app's unhead v1.
  // The storefront's own URL — the location index, not whatever sub-page is
  // being rendered — so every page of a location points at one entity.
  const { frontendUrl } = useRuntimeConfig().public;
  const { locale } = useI18n();
  const storefrontIdentifier = loc.full_identifier || loc.identifier;
  const storefrontUrl =
    frontendUrl && storefrontIdentifier
      ? `${frontendUrl}/${locale.value}/${storefrontIdentifier}`
      : undefined;

  const localBusinessLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": storefrontUrl,
    url: storefrontUrl,
    name: loc.name,
    description: desc,
    image: loc.cover || loc.logo || undefined,
    telephone:
      loc.full_mobile_number || (loc.phone ? String(loc.phone) : undefined),
    address: loc.address
      ? { "@type": "PostalAddress", streetAddress: loc.address }
      : undefined,
    geo:
      loc.latitude != null && loc.longitude != null
        ? {
          "@type": "GeoCoordinates",
          latitude: loc.latitude,
          longitude: loc.longitude,
        }
        : undefined,
  };
  useHead({
    script: [
      {
        type: "application/ld+json",
        // Strip undefined keys; escape "<" to avoid breaking out of <script>.
        innerHTML: JSON.stringify(
          Object.fromEntries(
            Object.entries(localBusinessLd).filter(([, v]) => v !== undefined),
          ),
        ).replace(/</g, "\\u003c"),
      },
    ],
  });

  business.value.integrations.forEach((integration) => {
    if (integration.key === "facebook_pixel") {
      const facebookPixel = useScriptMetaPixel({
        id: integration.metadata.pixel_id,
      });
      facebookPixel.fbq("track", "PageView");
    } else if (integration.key === "google_analytics") {
      useScriptGoogleAnalytics({
        id: integration.metadata.tracking_id,
      });
    }
  });
}
</script>

<template>
  <div class="h-screen lg:h-screen">
    <BusinessCover class="hidden lg:block" />
    <!-- lg:max-w-[576px] -->
    <div
      class="bg-white lg:fixed lg:inset-y-0 lg:left-1/2 lg:-translate-x-1/2 lg:transform z-20 w-full lg:max-w-[600px] h-full lg:h-[calc(100vh-124px)] lg:mb-10 lg:mt-21 min-h-0 lg:rounded-3xl overflow-hidden">
      <BusinessMenu />
      <Transition name="menu-overlay">
        <div
          v-if="isMenuOpen"
          class="fixed inset-0 bg-gray-950/40 backdrop-blur-[2px] z-[26]"
          @click="isMenuOpen = false" />
      </Transition>

      <UCard
        :ui="{
          base: 'flex flex-col h-full min-h-0',
          rounded: '',
          ring: '',
          body: {
            base: `business-scroll-container h-full min-h-0 overflow-y-auto ${isMenuOpen ? 'pointer-events-none' : ''}`,
            padding: bodyPadding,
          },
          header: {
            padding: 'px-0 py-0',
          },
          divide: 'divide-y divide-gray-100',
        }">
        <template #header>
          <div class="divide-y divide-gray-100">
            <BusinessNavbar class="px-3 py-3 sm:py-5" :class="{ '!px-5': business?.is_freemium }" />
            <BusinessSubHeader
              v-if="subheaderTitle"
              :title="subheaderTitle"
              :is-back-button-visible="isBackButtonVisible"
              :is-search-visable="(route.meta.keys as string[])?.includes('products') ||
                (route.meta.keys as string[])?.includes('services')
                " />
          </div>
          <UProgress
            v-if="progress || isLoading"
            animation="carousel"
            size="2xs"
            class="absolute left-0 w-full bottom-0" />
        </template>

        <slot />
      </UCard>

      <ClientOnly>
        <BusinessFooter
          v-if="(route.meta.keys as string[])?.includes('booking')" />

        <MembershipsFooter
          v-else-if="(route.meta.keys as string[])?.includes('memberships')" />

        <ProductsFooter
          v-else-if="(route.meta.keys as string[])?.includes('order')" />

        <GiftCardsFooter
          v-else-if="
            (route.meta.keys as string[])?.includes('gift-cards') &&
            giftCardSelected
          "
        />

        <AuthSignInDialog />

        <BusinessSectionSelectSheet />
      </ClientOnly>

      <div
        id="layout-drawer-root"
        class="pointer-events-none absolute inset-0 z-[60]" />
    </div>
  </div>
</template>
